import React, { useEffect, useState } from 'react';
import { fetchSystemConfig } from '../services/firebaseService';
import Header from './layout/Header';
import { Loader2, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../services/localeHelpers';

const TermsOfService: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    document.title = 'Brukervilkår – Fricamp';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Brukervilkår for Fricamp-applikasjonen. Les om vilkår for bruk av tjenesten.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://app.fricamp.com${window.location.pathname}`);

    return () => {
      document.title = 'Fricamp';
      metaDesc?.remove();
      canonical?.remove();
    };
  }, []);

  useEffect(() => {
    const loadTerms = async () => {
      try {
        const config = await fetchSystemConfig('terms_of_service');
        if (config) {
          const lang = i18n.language === 'no' ? 'no' : 'en';
          setContent(config[lang] || config['no'] || 'Brukervilkår er under oppdatering...');
          if (config.last_updated) {
            const ts = config.last_updated?.toDate?.() ?? new Date(config.last_updated);
            setLastUpdated(ts.toLocaleDateString(getDateLocale()));
          }
        } else {
          setContent('Brukervilkår er under oppdatering...');
        }
      } catch (error) {
        console.error('Failed to load terms of service:', error);
        setContent('Kunne ikke laste brukervilkårene.');
      } finally {
        setIsLoading(false);
      }
    };
    loadTerms();
  }, [i18n.language]);

  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        showSearch={false}
        searchQuery=""
        setSearchQuery={() => { }}
        searchResults={[]}
        onResultClick={() => { }}
        onCategoryClick={() => { }}
        currentUser={null}
        onAuthClick={() => { window.location.href = '/?login=1'; }}
        onLogoClick={() => { window.location.href = '/'; }}
        onShowProfile={() => { }}
        onLogout={() => { }}
        filterUnknownSurface={false}
        onToggleSurfaceFilter={() => { }}
        filterVerified={false}
        onToggleVerifiedFilter={() => { }}
        vehicleType="standard"
        onSetVehicleType={() => { }}
        isDev={false}
      />

      <main className="max-w-4xl mx-auto px-4 py-12 md:py-20">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 px-6 py-8 md:px-10 md:py-12 text-white">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="text-amber-400" size={32} />
              <h1 className="text-3xl md:text-4xl font-bold">{t("auto.termsofservice.brukervilkar", "Brukervilkår")}</h1>
            </div>
            <p className="text-slate-300 text-lg max-w-2xl">
              {t("auto.termsofservice.vilkar_for_bruk_av_fricamp_app", "Vilkår for bruk av Fricamp-applikasjonen fra Takle Consulting.")}
            </p>
          </div>

          <div className="px-6 py-10 md:px-12 md:py-16">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Loader2 className="animate-spin text-slate-400" size={48} />
                <p className="text-slate-500 font-medium">{t("auto.termsofservice.laster_innhold", "Laster innhold...")}</p>
              </div>
            ) : (
              <div className="prose prose-slate max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-p:text-slate-600 prose-p:leading-relaxed whitespace-pre-wrap">
                {content}
              </div>
            )}

            <div className="mt-16 pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-sm text-slate-400">
              <p>Sist oppdatert: {lastUpdated || new Date().toLocaleDateString(getDateLocale())}</p>
              <p>© {new Date().getFullYear()} Takle Consulting</p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <a
            href="/"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-200"
          >
            {t("auto.termsofservice.tilbake_til_kartet", "Tilbake til kartet")}
          </a>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
